var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useRef } from "react";
import { Tab } from "@bookingcom/bui-react";
import { useTabContext } from "@bookingcom/bui-react/components/Tab/Tab.context";
import useIsomorphicLayoutEffect from "@bookingcom/bui-react/hooks/useIsomorphicLayoutEffect";
import "./Header.types";
import { EVENTS, dispatchEvent } from "../events";
/**
 * Tabs will be considered visible
 * when 50% of each is exposed to the users in the viewport
 */
var options = {
    threshold: 0.5,
};
var useExposure = function (refs, tabs, moreRef) {
    var itemsRef = useRef([]);
    var callback = useCallback(function (entries, observer) {
        entries.forEach(function (entry) {
            var _a;
            var ele = itemsRef.current.find(function (item) { return item.ref === entry.target; });
            if (entry.isIntersecting && ele && !ele.exposed) {
                var tabItem = itemsRef.current.find(function (item) { return item.ref === entry.target; });
                if (process.env.NODE_ENV === "development") {
                    console.log("[web-shell] ".concat((_a = tabItem === null || tabItem === void 0 ? void 0 : tabItem.tab) === null || _a === void 0 ? void 0 : _a.id, " is intersecting"));
                }
                dispatchEvent(EVENTS.TAB_EXPOSED, tabItem === null || tabItem === void 0 ? void 0 : tabItem.tab);
                ele.exposed = true;
                observer.unobserve(entry.target);
            }
        });
    }, []);
    useIsomorphicLayoutEffect(function () {
        var fn = function () {
            if (itemsRef.current && itemsRef.current.length) {
                itemsRef.current.forEach(function (ele) {
                    var _a;
                    if (!ele.exposed) {
                        if (process.env.NODE_ENV === "development") {
                            console.log("[web-shell] ".concat((_a = ele === null || ele === void 0 ? void 0 : ele.tab) === null || _a === void 0 ? void 0 : _a.id, " is intersecting"));
                        }
                        dispatchEvent(EVENTS.TAB_EXPOSED, ele === null || ele === void 0 ? void 0 : ele.tab);
                        Object.assign(ele, { exposed: true });
                    }
                });
            }
        };
        if (moreRef.current) {
            moreRef.current.addEventListener("click", fn);
        }
        return function () {
            if (moreRef.current) {
                moreRef.current.removeEventListener("click", fn);
            }
        };
    }, [moreRef, itemsRef]);
    useIsomorphicLayoutEffect(function () {
        var observer;
        if (window && window.IntersectionObserver) {
            var initialItems = (refs.current || []).map(function (ref, idx) {
                var _a, _b, _c;
                return ({
                    ref: ref,
                    tab: tabs[idx],
                    exposed: (_c = (_b = (_a = itemsRef.current) === null || _a === void 0 ? void 0 : _a[idx]) === null || _b === void 0 ? void 0 : _b.exposed) !== null && _c !== void 0 ? _c : false,
                });
            });
            itemsRef.current = initialItems;
            observer = new IntersectionObserver(callback, options);
            if (itemsRef.current && itemsRef.current.length) {
                itemsRef.current.forEach(function (ele) {
                    if (!ele.exposed) {
                        observer.observe(ele.ref);
                    }
                });
            }
        }
        return function () {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [callback, refs, tabs]);
};
var TabList = function (_a) {
    var tabs = _a.tabs;
    var _b = useTabContext(), buttonRefs = _b.buttonRefs, moreRef = _b.moreRef;
    useExposure(buttonRefs, tabs, moreRef);
    return (React.createElement(Tab.TriggerList, null, tabs.map(function (tab) { return (React.createElement(Tab.Trigger, __assign({ key: tab.id, native: true }, tab, { linkAttributes: {
            role: "tab",
            id: tab.id,
        } }))); })));
};
export default TabList;
